import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import type { QuoteState } from '@/features/china-payment/useQuote.js';
import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';

/**
 * The live quote under the amount box (MYP-60): rate, service fee, the RM amount where the server
 * sends one (bank transfer), and the total to pay. Every figure is the server's (`alipay_rechange`)
 * — none is worked out here (MYP-64).
 *
 * While a new quote loads the previous one stays, dimmed, so the numbers update rather than
 * flicker. The fees can differ from the rate card's (China Payment API issues, item 2); the
 * footnote says staff confirm the final amount. The RM 100 minimum is the amount step's to say
 * (`AmountStep`), since it also stops the request from going on.
 */
export function QuoteSummary({ state }: { state: QuoteState }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { status, quote, stale, retry } = state;

  if (status === 'idle' || status === 'invalid') {
    return (
      <p className="rounded-lg bg-surface-alt px-4 py-3 text-sm text-muted">
        {t('chinaPayment.request.enterAmount')}
      </p>
    );
  }

  if (status === 'error') {
    return (
      <div className="grid justify-items-start gap-3 rounded-lg bg-surface-alt px-4 py-3">
        <p className="text-sm text-ink">{t('chinaPayment.request.quoteFailed')}</p>
        <button type="button" onClick={retry} className={buttonClass('secondary')}>
          <Icon name="refresh" size={18} />
          {t('actions.retry')}
        </button>
      </div>
    );
  }

  if (!quote) {
    return (
      <p aria-busy="true" className="rounded-lg bg-surface-alt px-4 py-3 text-sm text-muted">
        {t('chinaPayment.request.quoting')}
      </p>
    );
  }

  const rows = [
    {
      key: 'rate',
      label: t('chinaPayment.request.rate'),
      value: t('chinaPayment.request.rateValue', { rate: quote.rate }),
    },
    ...(quote.rmAmountCents === undefined
      ? []
      : [
          {
            key: 'rm',
            label: t('chinaPayment.request.rmAmount'),
            value: fmt.money(quote.rmAmountCents, 'RM'),
          },
        ]),
    {
      key: 'fee',
      label: t('chinaPayment.request.serviceFee'),
      value: fmt.money(quote.serviceFeeCents, quote.serviceFeeCurrency),
    },
  ];

  return (
    <div
      aria-busy={stale || undefined}
      className={cn(
        'rounded-lg bg-surface-alt px-4 py-3 transition-opacity',
        stale && 'opacity-60',
      )}
    >
      <dl className="grid gap-1.5 text-sm">
        {rows.map((row) => (
          <div key={row.key} className="flex justify-between gap-4">
            <dt className="text-muted">{row.label}</dt>
            <dd className="text-ink tabular-nums">{row.value}</dd>
          </div>
        ))}
        <div className="mt-1 flex items-baseline justify-between gap-4 border-t border-line pt-2">
          <dt className="font-medium text-ink">{t('chinaPayment.request.total')}</dt>
          {/* Announced when it settles, not on every keystroke's interim figure. */}
          <dd
            aria-live={stale ? 'off' : 'polite'}
            className="text-lg font-semibold text-brand tabular-nums"
          >
            {fmt.money(quote.totalRmCents, 'RM')}
          </dd>
        </div>
      </dl>
      <p className="mt-2 text-xs text-muted">{t('chinaPayment.request.staffConfirms')}</p>
    </div>
  );
}
